/* Lightweight buoyant body. Five hull stations sample the actual paint; their
   displaced fraction creates lift and torque. Fluid velocity supplies drag.
   Coordinates are document pixels, independent of the scrolling viewport. */
(function(root){
  'use strict';
  const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
  class NyeRaft {
    constructor(){this.reset();}
    reset(){this.active=false;this.x=0;this.y=0;this.vx=0;this.vy=0;this.angle=0;this.spin=0;this.wet=0;this.opacity=0;this.width=120;}
    launch(x,y,gravity,width){
      this.active=true;this.x=x;this.y=y;this.vx=0;this.vy=0;
      this.angle=-Math.atan2(gravity.x,gravity.y);this.spin=0;this.width=width;this.wet=0;
    }
    resize(oldWidth,oldHeight,width,height){
      if(!this.active)return;
      this.x*=width/oldWidth;this.y*=height/oldHeight;
      this.width=width<600?112:140;this.vx*=width/oldWidth;
    }
    step(dt,gravity,sample,bounds,hasPaint=true){
      if(!this.active)return;
      const duration=Math.min(.05,Math.max(0,dt)),steps=Math.max(1,Math.ceil(duration/(1/120))),t=duration/steps;
      this.opacity=clamp(this.opacity+(hasPaint?1.8:-1.8)*duration,0,1);
      if(!hasPaint&&this.opacity===0){this.reset();return;}
      for(let j=0;j<steps;j++)this.tick(t,gravity,sample,bounds);
    }
    tick(dt,g,sample,bounds){
      const c=Math.cos(this.angle),s=Math.sin(this.angle),inertia=this.width*this.width/10;
      let fx=g.x*640,fy=g.y*640,torque=0,wet=0;
      for(const station of [-.42,-.21,0,.21,.42]) {
        const rx=station*this.width*c,ry=station*this.width*s;
        let immersion=0,flowX=0,flowY=0;
        for(const depth of [-8,0,8]) {
          const v=sample(this.x+rx-s*depth,this.y+ry+c*depth);
          immersion+=clamp(v.wet,0,1)/3;flowX+=v.vx/3;flowY+=v.vy/3;
        }
        wet+=immersion/5;
        const localX=this.vx-this.spin*ry,localY=this.vy+this.spin*rx;
        const lift=1720*immersion/5;
        const drag=5.5*immersion/5;
        const bx=-g.x*lift+(clamp(flowX,-240,240)-localX)*drag;
        const by=-g.y*lift+(clamp(flowY,-240,240)-localY)*drag;
        fx+=bx;fy+=by;torque+=(rx*by-ry*bx)/inertia;
      }
      this.wet=wet;
      // Low center of buoyancy gives the raft a gentle righting tendency while
      // sampled wave heights still create most of its pitching motion.
      const target=-Math.atan2(g.x,g.y),error=Math.atan2(Math.sin(target-this.angle),Math.cos(target-this.angle));
      torque+=error*4*wet*Math.min(1,Math.hypot(g.x,g.y));
      this.vx=clamp((this.vx+fx*dt)*Math.exp(-.15*dt),-650,650);
      this.vy=clamp((this.vy+fy*dt)*Math.exp(-.15*dt),-650,650);
      this.spin=clamp((this.spin+torque*dt)*Math.exp(-1.8*dt),-3,3);
      this.x+=this.vx*dt;this.y+=this.vy*dt;this.angle+=this.spin*dt;
      const ex=Math.abs(Math.cos(this.angle))*this.width*.5+Math.abs(Math.sin(this.angle))*11;
      const ey=Math.abs(Math.sin(this.angle))*this.width*.5+Math.abs(Math.cos(this.angle))*11;
      if(this.x<ex){this.x=ex;this.vx=Math.abs(this.vx)*.12;this.spin*=.7;}
      if(this.x>bounds.width-ex){this.x=bounds.width-ex;this.vx=-Math.abs(this.vx)*.12;this.spin*=.7;}
      if(this.y<ey){this.y=ey;this.vy=Math.abs(this.vy)*.12;this.spin*=.7;}
      if(this.y>bounds.height-ey){this.y=bounds.height-ey;this.vy=-Math.abs(this.vy)*.12;this.spin*=.7;}
    }
  }
  class ShakeDetector {
    constructor(){this.reset();}
    reset(){this.start=null;this.last=0;this.first=null;this.cooldown=0;this.base=null;}
    feed(now,acc,total){
      const valid=a=>a&&['x','y','z'].every(k=>typeof a[k]==='number'&&Number.isFinite(a[k]));
      if(!valid(total))return null;
      if(this.start===null){this.start=now;this.last=now;this.base={x:total.x,y:total.y,z:total.z};return null;}
      const dt=Math.max(.001,Math.min(.1,(now-this.last)/1000));this.last=now;
      const v=valid(acc)?{x:acc.x,y:acc.y,z:acc.z}:{x:total.x-this.base.x,y:total.y-this.base.y,z:total.z-this.base.z};
      const smooth=1-Math.exp(-dt/.65);
      for(const k of ['x','y','z'])this.base[k]+=(total[k]-this.base[k])*smooth;
      if(now-this.start<650||now<this.cooldown)return null;
      // Rotation alone changes gravity's direction, not its magnitude. This
      // gate prevents tilt-only events from tripping the no-linear-data fallback.
      if(!valid(acc)&&Math.abs(Math.hypot(total.x,total.y,total.z)-9.81)<4.5)return null;
      const magnitude=Math.hypot(v.x,v.y,v.z);
      if(magnitude<12)return null;
      if(this.first&&now-this.first.time>=65&&now-this.first.time<650){
        const dot=(v.x*this.first.x+v.y*this.first.y+v.z*this.first.z)/(magnitude*this.first.magnitude);
        if(dot<-.25){this.first=null;this.cooldown=now+3500;return {strength:Math.min(2,magnitude/12),x:v.x,y:v.y,z:v.z};}
      }
      if(!this.first||now-this.first.time>=650)this.first={...v,time:now,magnitude};
      return null;
    }
  }
  class Rider {
    constructor(){this.body=new NyeRaft();this.reset();}
    reset(){this.state='aboard';this.age=0;this.body.reset();this.climb=null;this.grip=null;this.pipe=null;}
    grab(raft,g,point){
      if(!raft.active)return false;
      if(this.state==='aboard'){
        const n=Math.hypot(g.x,g.y)||1;
        this.body.launch(raft.x-g.x/n*35,raft.y-g.y/n*35,g,38);
        this.body.angle=raft.angle;this.body.opacity=raft.opacity;
      }
      this.state='held';this.age=0;this.climb=null;this.grip={x:point.x,y:point.y};return true;
    }
    enterDrain(point,exit){
      if(this.state!=='held')return false;
      this.pipe={from:{x:this.body.x,y:this.body.y},point:{...point},exit:{...exit}};
      this.grip=null;this.state='drain';this.age=0;return true;
    }
    moveGrip(point){if(this.state==='held')this.grip={x:point.x,y:point.y};}
    release(raft,g,cancelled=false){
      if(this.state!=='held')return false;
      this.grip=null;this.state='falling';this.age=0;
      this.body.vx=clamp(cancelled?0:this.body.vx,-420,420);
      this.body.vy=clamp(cancelled?0:this.body.vy,-420,420);
      this.body.spin=clamp(this.body.spin,-2,2);
      // Releasing the character directly over the raft offers a gentle catch.
      const c=Math.cos(raft.angle),s=Math.sin(raft.angle),dx=this.body.x-raft.x,dy=this.body.y-raft.y;
      const across=dx*c+dy*s,above=-dx*s+dy*c;
      if(!cancelled&&Math.abs(across)<raft.width*.4&&above>-100&&above<-15&&Math.hypot(this.body.vx,this.body.vy)<180)this.rescue();
      return true;
    }
    knock(raft,g,strength=1,direction=1){
      if(!raft.active||this.state!=='aboard')return false;
      const n=Math.hypot(g.x,g.y)||1,gx=g.x/n,gy=g.y/n;
      this.body.launch(raft.x-gx*35,raft.y-gy*35,g,38);
      this.body.opacity=raft.opacity;this.body.angle=raft.angle;
      this.body.vx=raft.vx+gy*direction*170*strength-gx*140;
      this.body.vy=raft.vy-gx*direction*170*strength-gy*140;
      this.body.spin=direction*1.5;this.state='falling';this.age=0;return true;
    }
    rescue(){
      if(this.state==='aboard'||this.state==='climbing')return false;
      this.climb={x:this.body.x,y:this.body.y,angle:this.body.angle};this.state='climbing';this.age=0;return true;
    }
    resize(ow,oh,w,h){
      this.body.resize(ow,oh,w,h);this.body.width=38;
      if(this.climb){this.climb.x*=w/ow;this.climb.y*=h/oh;}
      if(this.grip){this.grip.x*=w/ow;this.grip.y*=h/oh;}
    }
    step(dt,g,sample,bounds,raft,hasPaint){
      if(this.state==='aboard')return false;
      if(!raft.active){this.reset();return false;}
      this.age+=dt;
      if(this.state==='drain'){
        const t=Math.min(1,this.age/.55),p=this.pipe,b=this.body;
        b.x=p.from.x+(p.point.x-p.from.x)*t;b.y=p.from.y+(p.point.y-p.from.y)*t;b.angle=t*Math.PI*2;b.opacity=1-t;
        if(t===1){b.launch(p.exit.x,p.exit.y,g,38);b.opacity=1;b.vx=p.exit.vx;b.vy=-160;b.spin=2.5;this.pipe=null;this.state='falling';this.age=0;}
        return false;
      }
      if(this.state==='held'){
        const n=Math.hypot(g.x,g.y),gx=n>.1?g.x/n:0,gy=n>.1?g.y/n:1;
        const target={x:this.grip.x+gx*62,y:this.grip.y+gy*62};
        const steps=Math.max(1,Math.ceil(Math.min(dt,.05)/(1/120))),t=Math.min(dt,.05)/steps,b=this.body;
        for(let i=0;i<steps;i++){
          b.vx=clamp(b.vx+((target.x-b.x)*85-b.vx*14)*t,-700,700);
          b.vy=clamp(b.vy+((target.y-b.y)*85-b.vy*14)*t,-700,700);
          b.x=clamp(b.x+b.vx*t,48,bounds.width-48);b.y=clamp(b.y+b.vy*t,30,bounds.height-20);
          const angle=-Math.atan2(gx,gy)+clamp((b.vx*gy-b.vy*gx)*-.0015,-.45,.45);
          const error=Math.atan2(Math.sin(angle-b.angle),Math.cos(angle-b.angle));
          b.spin=clamp(b.spin+(error*35-b.spin*9)*t,-3,3);b.angle+=b.spin*t;
        }
        return false;
      }
      if(this.state==='climbing'){
        const t=Math.min(1,this.age/.9),ease=t*t*(3-2*t),n=Math.hypot(g.x,g.y)||1;
        const gx=g.x/n,gy=g.y/n,tx=raft.x-gx*35,ty=raft.y-gy*35;
        this.body.x=this.climb.x+(tx-this.climb.x)*ease-gx*Math.sin(Math.PI*t)*30;
        this.body.y=this.climb.y+(ty-this.climb.y)*ease-gy*Math.sin(Math.PI*t)*30;
        const da=Math.atan2(Math.sin(raft.angle-this.climb.angle),Math.cos(raft.angle-this.climb.angle));
        this.body.angle=this.climb.angle+da*ease;
        if(t===1)this.reset();return false;
      }
      this.body.step(dt,g,sample,bounds,hasPaint);
      if(!this.body.active){this.reset();return false;}
      if(this.state==='falling'&&this.body.wet>.12){this.state='swimming';this.age=0;return true;}
      if(this.state==='swimming'){
        // Small paddling impulses act through the same buoyant body, rather
        // than assigning a decorative screen-space bobbing position.
        const kick=Math.sin(this.age*7)*18*dt;
        this.body.vx-=g.x*kick;this.body.vy-=g.y*kick;
      }
      return false;
    }
  }
  NyeRaft.ShakeDetector=ShakeDetector;NyeRaft.Rider=Rider;
  if(typeof module!=='undefined'&&module.exports)module.exports=NyeRaft;else root.NyeRaft=NyeRaft;
})(typeof window!=='undefined'?window:globalThis);
